import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { API } from "../utils/apk";

const months = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
];

const isValidDate = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;

  const [year, month, day] = value.split("-").map(Number);
  const parsedDate = new Date(Date.UTC(year, month - 1, day));

  return (
    parsedDate.getUTCFullYear() === year &&
    parsedDate.getUTCMonth() === month - 1 &&
    parsedDate.getUTCDate() === day
  );
};

const getKolkataCalendar = () => {
  const parts = new Intl.DateTimeFormat("en", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })
    .formatToParts(new Date())
    .reduce((calendar, part) => ({ ...calendar, [part.type]: part.value }), {});

  return {
    today: `${parts.year}-${parts.month}-${parts.day}`,
    year: parts.year,
  };
};

const DeliveryBoyReport = () => {
  const { today, year: currentYear } = getKolkataCalendar();
  const [deliveryBoy, setDeliveryBoy] = useState("");
  const [deliveryBoyList, setDeliveryBoyList] = useState([]);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [month, setMonth] = useState("");
  const [year, setYear] = useState(currentYear);
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [hasLoadedReport, setHasLoadedReport] = useState(false);
  const [validationError, setValidationError] = useState("");

  const rowColors = [
    "bg-yellow-100",
    "bg-blue-100",
    "bg-green-100",
    "bg-pink-100",
    "bg-purple-100",
    "bg-orange-100",
    "bg-teal-100",
    "bg-red-100",
  ];

  // Fetch Delivery Boys
  const fetchDeliveryBoys = async () => {
    try {
      const response = await fetch(`${API}/get/all/delivery/boy`);
      const result = await response.json();
      if (result.success) setDeliveryBoyList(result.data);
    } catch (e) {
      console.log("Fetch delivery boy error:", e);
    }
  };

  useEffect(() => {
    fetchDeliveryBoys();
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!deliveryBoy) {
      setReportData([]);
      setLoading(false);
      setError(false);
      setHasLoadedReport(false);
      setValidationError("");
      return undefined;
    }

    const hasPartialRange = Boolean(startDate) !== Boolean(endDate);
    const hasInvalidRange =
      startDate &&
      endDate &&
      (!isValidDate(startDate) ||
        !isValidDate(endDate) ||
        startDate > endDate);

    if (hasPartialRange || hasInvalidRange) {
      setReportData([]);
      setLoading(false);
      setError(false);
      setHasLoadedReport(false);
      setValidationError(
        hasPartialRange
          ? "Select both a From Date and a To Date"
          : "From Date must be on or before To Date"
      );
      return undefined;
    }

    const fetchReport = async () => {
      setReportData([]);
      setLoading(true);
      setError(false);
      setHasLoadedReport(false);
      setValidationError("");

      const params = new URLSearchParams({ deliveryBoyId: deliveryBoy });
      if (startDate && endDate) {
        params.set("startDate", startDate);
        params.set("endDate", endDate);
      } else if (months.includes(month) && /^\d{4}$/.test(year)) {
        params.set("month", month);
        params.set("year", year);
      }

      try {
        const res = await fetch(
          `${API}/get/delivery/boy/report?${params.toString()}`
        );

        if (!res.ok) {
          throw new Error("Unable to load delivery report");
        }

        const result = await res.json();
        if (!result.success || !Array.isArray(result.data)) {
          throw new Error("Unable to load delivery report");
        }

        if (!cancelled) {
          setReportData(result.data);
          setHasLoadedReport(true);
        }
      } catch (e) {
        if (!cancelled) {
          setError(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchReport();

    return () => {
      cancelled = true;
    };
  }, [deliveryBoy, startDate, endDate, month, year]);

  const grandTotal = reportData.reduce(
    (t, o) => t + (o.orderData?.totalPrice || 0),
    0
  );

  return (
    <div className="p-6 bg-blue-200 min-h-screen">
      <h2 className="text-center text-xl font-bold text-white bg-indigo-800 py-2 rounded-md">
        DELIVERY BOY REPORT
      </h2>

      {/* FILTERS */}
      <div className="flex gap-4 justify-end p-2">
        {/* DELIVERY BOY DROPDOWN */}
        <select
          value={deliveryBoy}
          onChange={(e) => setDeliveryBoy(e.target.value)}
          className="px-2 py-1 w-48 rounded text-black"
        >
          <option value="">Select Delivery Boy</option>
          {deliveryBoyList.map((boy) => (
            <option key={boy._id} value={boy._id}>
              {boy.name} ({boy.mobileNo})
            </option>
          ))}
        </select>

        {/* DATE RANGE */}
        <div className="flex flex-col">
          <label
            htmlFor="delivery-report-start-date"
            className="text-xs font-semibold text-gray-700"
          >
            From Date
          </label>
          <input
            id="delivery-report-start-date"
            type="date"
            value={startDate}
            max={endDate || undefined}
            onChange={(e) => {
              setStartDate(e.target.value);
              setMonth("");
            }}
            className="px-2 py-1 w-36 rounded text-black"
          />
        </div>
        <div className="flex flex-col">
          <label
            htmlFor="delivery-report-end-date"
            className="text-xs font-semibold text-gray-700"
          >
            To Date
          </label>
          <input
            id="delivery-report-end-date"
            type="date"
            value={endDate}
            min={startDate || undefined}
            max={today}
            onChange={(e) => {
              setEndDate(e.target.value);
              setMonth("");
            }}
            className="px-2 py-1 w-36 rounded text-black"
          />
        </div>

        {/* MONTH AND YEAR */}
        <select
          aria-label="Month"
          value={month}
          onChange={(e) => {
            setMonth(e.target.value);
            if (e.target.value) {
              setStartDate("");
              setEndDate("");
            }
          }}
          className="px-2 py-1 w-36 rounded text-black"
        >
          <option value="">Select Month</option>
          {months.map((m) => (
            <option key={m} value={m}>
              {m.toUpperCase()}
            </option>
          ))}
        </select>
        <select
          aria-label="Year"
          value={year}
          onChange={(e) => setYear(e.target.value)}
          className="px-2 py-1 w-24 rounded text-black"
        >
          {[year - 1, year, Number(year) + 1].map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      </div>

      {validationError && (
        <div role="alert" className="mb-2 text-center text-red-700">
          {validationError}
        </div>
      )}

      {/* TABLE */}
      <div className="overflow-x-auto bg-white rounded shadow">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-200 font-semibold">
            <tr>
              <th className="p-2">Order ID</th>
              <th className="p-2">Item</th>
              <th className="p-2">Rate</th>
              <th className="p-2">Qty</th>
              <th className="p-2">Customer</th>
              <th className="p-2">Mobile</th>
              <th className="p-2">Total</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="p-4 text-center">
                  Loading delivery report...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="7" className="p-4 text-center text-red-600">
                  Unable to load delivery report
                </td>
              </tr>
            ) : hasLoadedReport && reportData.length === 0 ? (
              <tr>
                <td colSpan="7" className="p-4 text-center">
                  No records found
                </td>
              </tr>
            ) : (
              reportData.map((order, i) => {
              const orderInfo = order.orderData;

              const items =
                Array.isArray(orderInfo?.cartItems) && orderInfo.cartItems.length > 0
                  ? orderInfo.cartItems
                  : Array.isArray(orderInfo?.addToCart?.cartIds)
                    ? orderInfo.addToCart.cartIds
                    : [];

              const rowColor = rowColors[i % rowColors.length];
              const rowspan = items.length;

              return items.map((item, idx) => (
                <tr key={`${i}-${idx}`} className={rowColor}>
                  {idx === 0 && (
                    <td rowSpan={rowspan} className="p-2 font-bold">
                      {orderInfo.orderId}
                    </td>
                  )}

                  <td className="p-2">{item.itemName}</td>
                  <td className="p-2">{item.itemRate}</td>
                  <td className="p-2">{item.count}</td>

                  {idx === 0 && (
                    <>
                      <td rowSpan={rowspan} className="p-2">
                        {orderInfo.userAddress?.[0]?.name}
                      </td>
                      <td rowSpan={rowspan} className="p-2">
                        {orderInfo.userAddress?.[0]?.mobile}
                      </td>
                      <td
                        rowSpan={rowspan}
                        className="p-2 font-bold text-green-700"
                      >
                        ₹{orderInfo.totalPrice}
                      </td>
                    </>
                  )}
                </tr>
              ));
              })
            )}
          </tbody>
        </table>
      </div>

      {/* FOOTER */}
      <div className="flex justify-between items-center mt-4 px-4">
        <div className="font-semibold text-lg">
          Total Delivered:{" "}
          <span className="text-green-700 font-bold">₹{grandTotal}</span>
        </div>
      </div>
    </div>
  );
};

export default DeliveryBoyReport;
